import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Aviso,
  Botao,
  Campo,
  CampoDeSelecao,
  Cartao,
  CelulaDeAcoes,
  MenuDeAcoes,
  Modal,
  Selo,
  useDialogo,
} from '../../componentes/ui';
import { ConfirmarVariaveis } from '../../componentes/ConfirmarVariaveis';
import { EditorDeContrato, type EditorDeContratoHandle } from '../../componentes/EditorDeContrato';
import {
  AvisoDeVariaveisDesconhecidas,
  PainelDeVariaveis,
} from '../../componentes/VariaveisDoModelo';
import { lerComoBase64 } from '../../servicos/arquivo';
import { requisicaoAutenticada } from '../../servicos/http';
import {
  comExemplos,
  tokensDesconhecidos,
  type ModeloGeral,
  type ModeloGeralResumido,
  type ModelosGerais,
  type UsoComVariaveis,
  type UsoDoModelo,
} from '../../servicos/modelosDeContrato';
import { ErroDaApi } from '../ErroDaApi';

const CHAVE = ['contratos-modelos'] as const;

/**
 * A aba Contratos da Central de Configurações (D-020).
 *
 * ## Um modelo diz onde é usado
 *
 * Cada modelo tem um uso — o contrato do Recorrente, do Pontual, do Passivo,
 * ou o termo de comissionamento — e a lista vem separada por eles, que é como
 * a operação procura ("o termo", "o contrato do Pontual"). No fechamento de
 * uma negociação, os modelos daquele uso são oferecidos para escolher.
 *
 * ## As variáveis são as do uso
 *
 * O painel ao lado do editor mostra só as variáveis do uso escolhido, cada uma
 * com o que ela é. O que estiver escrito entre colchetes e não for variável do
 * uso **some do PDF** (RN-043) — por isso a tela aponta essas antes de salvar.
 *
 * Os modelos que cada empresa tem no próprio cadastro continuam lá: estes são
 * os gerais, que valem para qualquer negociação.
 */
export function ModelosDeContrato(): JSX.Element {
  const queryClient = useQueryClient();
  const dialogo = useDialogo();
  // `null` = lista; `{ id }` = editando; `{ id: null, uso }` = criando.
  const [aberto, setAberto] = useState<{ id: string | null; uso?: UsoDoModelo } | null>(null);
  const [importando, setImportando] = useState(false);

  const dados = useQuery({
    queryKey: CHAVE,
    queryFn: () => requisicaoAutenticada<ModelosGerais>('/contratos/modelos'),
  });

  const excluir = useMutation({
    mutationFn: (id: string) =>
      requisicaoAutenticada<void>(`/contratos/modelos/${id}`, { metodo: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: CHAVE }),
  });

  if (dados.isError) {
    return <ErroDaApi titulo="Não foi possível carregar os modelos" erro={dados.error} />;
  }
  if (dados.data === undefined) {
    return <p className="py-10 text-center text-sm text-conteudo-sutil">Carregando modelos…</p>;
  }
  const { modelos, usos } = dados.data;

  if (aberto !== null) {
    return (
      <EditorDoModelo
        key={aberto.id ?? `novo-${aberto.uso ?? ''}`}
        modeloId={aberto.id}
        usoInicial={aberto.uso ?? usos[0]!.id}
        usos={usos}
        aoVoltar={() => setAberto(null)}
        aoCriar={(id) => setAberto({ id })}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <p className="max-w-3xl text-sm text-conteudo-secundario">
          Os textos que viram PDF. Cada modelo diz onde é usado e, no fechamento da negociação, você
          escolhe qual usar. As variáveis entre colchetes viram os dados da negociação.
        </p>
        <div className="flex shrink-0 flex-wrap gap-2">
          <Botao variante="secundario" onClick={() => setImportando(true)}>
            Importar do Word
          </Botao>
          <Botao onClick={() => setAberto({ id: null })}>+ Novo modelo</Botao>
        </div>
      </div>

      {excluir.isError && <ErroDaApi titulo="Não foi possível excluir" erro={excluir.error} />}

      {usos.map((uso) => {
        const doUso = modelos.filter((m) => m.uso === uso.id);
        return (
          <Cartao
            key={uso.id}
            como="section"
            titulo={uso.rotulo}
            subtitulo={uso.descricao}
            densidade="compacta"
            acao={
              <Botao
                variante="sutil"
                tamanho="pequeno"
                onClick={() => setAberto({ id: null, uso: uso.id })}
              >
                + Criar
              </Botao>
            }
          >
            {!uso.geradoPeloDeca && (
              <p className="mb-3 text-xs text-conteudo-sutil">
                O Deca ainda não gera este documento — o modelo fica pronto para quando gerar.
              </p>
            )}
            {doUso.length === 0 ? (
              <p className="text-sm text-conteudo-sutil">Nenhum modelo ainda.</p>
            ) : (
              <ul className="flex flex-col divide-y divide-borda">
                {doUso.map((m) => (
                  <LinhaDoModelo
                    key={m.id}
                    modelo={m}
                    aoAbrir={() => setAberto({ id: m.id })}
                    aoExcluir={() => {
                      void (async () => {
                        const ok = await dialogo.confirmar({
                          titulo: `Excluir o modelo "${m.nome}"?`,
                          descricao:
                            'Ele deixa de ser oferecido nos fechamentos. Os PDFs já gerados com ele continuam como estão.',
                          rotuloConfirmar: 'Excluir',
                          tom: 'perigo',
                        });
                        if (ok) excluir.mutate(m.id);
                      })();
                    }}
                  />
                ))}
              </ul>
            )}
          </Cartao>
        );
      })}

      <ModalDeImportacao
        aberto={importando}
        usos={usos}
        aoFechar={() => setImportando(false)}
        aoImportar={(modelo) => {
          setImportando(false);
          void queryClient.invalidateQueries({ queryKey: CHAVE });
          setAberto({ id: modelo.id });
        }}
      />
    </div>
  );
}

function LinhaDoModelo(props: {
  readonly modelo: ModeloGeralResumido;
  readonly aoAbrir: () => void;
  readonly aoExcluir: () => void;
}): JSX.Element {
  const m = props.modelo;
  return (
    <li className="flex items-center justify-between gap-3 py-2">
      <span className="flex min-w-0 flex-col">
        <button
          type="button"
          onClick={props.aoAbrir}
          className="truncate text-left text-sm text-conteudo-primario underline-offset-4 hover:text-acento-ciano hover:underline"
        >
          {m.nome}
        </button>
        <span className="text-xs text-conteudo-sutil">
          {m.arquivoOrigem !== null && <>Importado de {m.arquivoOrigem} · </>}
          Alterado em {new Date(m.atualizadoEm).toLocaleDateString('pt-BR')}
        </span>
      </span>
      <span className="flex shrink-0 items-center gap-3">
        <Selo tom={m.temConteudo ? 'positivo' : 'neutro'} tamanho="pequeno">
          {m.temConteudo ? 'Com texto' : 'Vazio'}
        </Selo>
        <CelulaDeAcoes>
          <MenuDeAcoes
            rotulo={m.nome}
            itens={[
              { rotulo: 'Editar', aoEscolher: props.aoAbrir },
              {
                rotulo: 'Excluir',
                perigo: true,
                descricao: 'Os contratos já gerados não mudam.',
                aoEscolher: props.aoExcluir,
              },
            ]}
          />
        </CelulaDeAcoes>
      </span>
    </li>
  );
}

/* ─── Editor ──────────────────────────────────────────────────────────────── */

interface Rascunho {
  readonly nome: string;
  readonly uso: UsoDoModelo;
  readonly conteudo: string;
}

function EditorDoModelo(props: {
  /** `null` cria. */
  readonly modeloId: string | null;
  readonly usoInicial: UsoDoModelo;
  readonly usos: readonly UsoComVariaveis[];
  readonly aoVoltar: () => void;
  /** Criado, o editor continua aberto — agora no modelo gravado. */
  readonly aoCriar: (id: string) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const { modeloId } = props;
  const editorRef = useRef<EditorDeContratoHandle>(null);
  const [verHtml, setVerHtml] = useState(false);
  const [previa, setPrevia] = useState(false);
  /**
   * D-022 — as variáveis novas, confirmadas antes de salvar (`'salvar'`) ou
   * pelo atalho do aviso (`'so-criar'`).
   */
  const [confirmando, setConfirmando] = useState<'salvar' | 'so-criar' | null>(null);
  const [form, setForm] = useState<Rascunho | null>(
    modeloId === null ? { nome: '', uso: props.usoInicial, conteudo: '' } : null,
  );

  const modelo = useQuery({
    queryKey: ['contratos-modelo', modeloId],
    queryFn: () => requisicaoAutenticada<ModeloGeral>(`/contratos/modelos/${modeloId ?? ''}`),
    enabled: modeloId !== null,
  });

  // O rascunho nasce do gravado, e só quando ele chega: começar vazio e salvar
  // nesse instante apagaria o texto.
  useEffect(() => {
    if (modelo.data !== undefined && form === null) {
      setForm({ nome: modelo.data.nome, uso: modelo.data.uso, conteudo: modelo.data.conteudo });
    }
  }, [modelo.data, form]);

  const salvar = useMutation({
    mutationFn: (dados: Rascunho) =>
      modeloId === null
        ? requisicaoAutenticada<ModeloGeral>('/contratos/modelos', { metodo: 'POST', corpo: dados })
        : requisicaoAutenticada<ModeloGeral>(`/contratos/modelos/${modeloId}`, {
            metodo: 'PUT',
            corpo: dados,
          }),
    onSuccess: (gravado) => {
      queryClient.setQueryData(['contratos-modelo', gravado.id], gravado);
      void queryClient.invalidateQueries({ queryKey: CHAVE });
      if (modeloId === null) props.aoCriar(gravado.id);
      else setForm({ nome: gravado.nome, uso: gravado.uso, conteudo: gravado.conteudo });
    },
  });

  if (modelo.isError) {
    return <ErroDaApi titulo="Não foi possível carregar o modelo" erro={modelo.error} />;
  }
  if (form === null) {
    return <p className="py-10 text-center text-sm text-conteudo-sutil">Carregando modelo…</p>;
  }

  const uso = props.usos.find((u) => u.id === form.uso) ?? props.usos[0]!;
  const gravado = modelo.data;
  const alterado =
    gravado === undefined
      ? form.nome !== '' || form.conteudo !== ''
      : form.nome !== gravado.nome ||
        form.uso !== gravado.uso ||
        form.conteudo !== gravado.conteudo;
  const mudar = (parte: Partial<Rascunho>): void =>
    setForm((f) => (f === null ? f : { ...f, ...parte }));
  const inserir = (token: string): void => {
    if (verHtml) mudar({ conteudo: `${form.conteudo}[${token}]` });
    else editorRef.current?.inserir(`[${token}]`);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <button
            type="button"
            onClick={props.aoVoltar}
            className="text-sm text-conteudo-secundario underline-offset-4 hover:text-conteudo-primario hover:underline"
          >
            ← Modelos de contrato
          </button>
          <h2 className="mt-2 font-display text-lg font-semibold tracking-tight">
            {modeloId === null ? 'Novo modelo' : gravado?.nome}
          </h2>
          {gravado !== undefined && (
            <p className="mt-1 text-sm text-conteudo-secundario">
              {gravado.arquivoOrigem !== null && <>Importado de {gravado.arquivoOrigem}. </>}
              Alterado pela última vez em{' '}
              {new Date(gravado.atualizadoEm).toLocaleDateString('pt-BR')}.
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {form.conteudo.trim() === '' && uso.esqueleto !== null && (
            <Botao variante="secundario" onClick={() => mudar({ conteudo: uso.esqueleto ?? '' })}>
              Partir do esqueleto padrão
            </Botao>
          )}
          <Botao
            variante="secundario"
            disabled={form.conteudo.trim() === ''}
            onClick={() => setPrevia(true)}
          >
            Ver com exemplo
          </Botao>
          <Botao
            onClick={() =>
              tokensDesconhecidos(form.conteudo, uso).length > 0
                ? setConfirmando('salvar')
                : salvar.mutate(form)
            }
            disabled={form.nome.trim() === '' || !alterado}
            carregando={salvar.isPending}
          >
            {modeloId === null ? 'Criar modelo' : 'Salvar modelo'}
          </Botao>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Campo
          rotulo="Nome do modelo"
          required
          placeholder="Termo de comissionamento padrão, Contrato Pontual 2027…"
          value={form.nome}
          onChange={(e) => mudar({ nome: e.target.value })}
        />
        <CampoDeSelecao
          rotulo="Onde vou usar"
          ajuda={uso.descricao}
          value={form.uso}
          onChange={(e) => mudar({ uso: e.target.value as UsoDoModelo })}
          opcoes={props.usos.map((u) => ({ valor: u.id, rotulo: u.rotulo }))}
        />
      </div>

      {!uso.geradoPeloDeca && (
        <Aviso tom="informacao" titulo="Ainda não usado pelo Deca">
          O Deca ainda não gera este documento. O modelo fica guardado e passa a ser oferecido assim
          que a geração chegar.
        </Aviso>
      )}
      <AvisoDeVariaveisDesconhecidas
        conteudo={form.conteudo}
        uso={uso}
        aoCriar={() => setConfirmando('so-criar')}
      />
      {confirmando !== null && (
        <ConfirmarVariaveis
          onde={{ contrato: uso.id }}
          tokens={tokensDesconhecidos(form.conteudo, uso)}
          rotuloConfirmar={confirmando === 'salvar' ? 'Confirmar e salvar' : 'Criar variáveis'}
          aoCancelar={() => setConfirmando(null)}
          aoConcluir={() => {
            void queryClient.invalidateQueries({ queryKey: CHAVE });
            if (confirmando === 'salvar') salvar.mutate(form);
            setConfirmando(null);
          }}
        />
      )}
      {salvar.isSuccess && !alterado && (
        <Aviso tom="positivo" titulo="Modelo salvo">
          Os próximos documentos gerados com este modelo usam este texto. Os já gerados não mudam.
        </Aviso>
      )}
      {salvar.isError && <ErroDaApi titulo="Não foi possível salvar" erro={salvar.error} />}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_20rem]">
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-medium text-conteudo-primario">
              Texto
              {alterado && <span className="ml-2 text-xs text-atencao">alterações não salvas</span>}
            </span>
            <button
              type="button"
              onClick={() => setVerHtml((x) => !x)}
              className="text-xs text-conteudo-secundario underline-offset-4 hover:text-conteudo-primario hover:underline"
            >
              {verHtml ? 'Voltar ao editor' : 'Ver HTML'}
            </button>
          </div>
          {verHtml ? (
            <textarea
              value={form.conteudo}
              onChange={(e) => mudar({ conteudo: e.target.value })}
              spellCheck={false}
              rows={26}
              className="valor-fiscal w-full rounded-card border border-borda bg-superficie-base p-3 text-xs leading-relaxed text-conteudo-primario focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano"
            />
          ) : (
            <EditorDeContrato
              ref={editorRef}
              value={form.conteudo}
              onChange={(html) => mudar({ conteudo: html })}
              variaveis={uso.grupos.flatMap((g) =>
                g.variaveis.map((v) => ({ texto: `[${v.token}]`, rotulo: v.descricao })),
              )}
            />
          )}
          <span className="text-xs text-conteudo-sutil">
            Escreva como no Word. Um modelo com o texto vazio não é oferecido no fechamento.
          </span>
        </div>

        <PainelDeVariaveis uso={uso} aoInserir={inserir} />
      </div>

      <Modal
        aberto={previa}
        aoFechar={() => setPrevia(false)}
        titulo="Com valores de exemplo"
        descricao="Em azul, as variáveis já trocadas pelo exemplo; em vermelho, as que somem do PDF."
        largura="ampla"
      >
        {previa && (
          <iframe
            title="Prévia do modelo de contrato"
            sandbox=""
            srcDoc={`<!doctype html><html><body style="font-family:Georgia,serif;font-size:14px;line-height:1.6;padding:24px 32px;color:#111">${comExemplos(form.conteudo, uso)}</body></html>`}
            className="h-[70vh] w-full rounded-card border border-borda bg-white"
          />
        )}
      </Modal>
    </div>
  );
}

/* ─── Importar do Word ────────────────────────────────────────────────────── */

function ModalDeImportacao(props: {
  readonly aberto: boolean;
  readonly usos: readonly UsoComVariaveis[];
  readonly aoFechar: () => void;
  readonly aoImportar: (modelo: ModeloGeral) => void;
}): JSX.Element {
  const [nome, setNome] = useState('');
  const [uso, setUso] = useState<UsoDoModelo>(props.usos[0]?.id ?? 'CONTRATO_RECORRENTE');
  const [arquivo, setArquivo] = useState<File | null>(null);

  const importar = useMutation({
    mutationFn: async () => {
      if (arquivo === null) throw new Error('Escolha o arquivo .docx.');
      return requisicaoAutenticada<ModeloGeral>('/contratos/modelos/importar-word', {
        metodo: 'POST',
        corpo: {
          nome: nome.trim() === '' ? arquivo.name.replace(/\.docx$/i, '') : nome.trim(),
          uso,
          arquivo: await lerComoBase64(arquivo),
          nomeDoArquivo: arquivo.name,
        },
      });
    },
    onSuccess: (modelo) => {
      setNome('');
      setArquivo(null);
      props.aoImportar(modelo);
    },
  });

  const fechar = (): void => {
    importar.reset();
    props.aoFechar();
  };
  const docx = arquivo !== null && /\.docx$/i.test(arquivo.name);

  return (
    <Modal
      aberto={props.aberto}
      aoFechar={fechar}
      titulo="Importar modelo do Word"
      descricao="O texto do .docx vira o modelo; as [VARIÁVEIS] digitadas no Word continuam valendo."
      rodape={
        <>
          <Botao variante="secundario" onClick={fechar}>
            Cancelar
          </Botao>
          <Botao onClick={() => importar.mutate()} disabled={!docx} carregando={importar.isPending}>
            Importar
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-2">
          <span className="text-sm font-medium text-conteudo-secundario">Arquivo Word (.docx)</span>
          <input
            type="file"
            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
            className="text-sm text-conteudo-secundario file:mr-3 file:rounded-card file:border file:border-borda file:bg-superficie-base file:px-3 file:py-2 file:text-sm file:text-conteudo-primario"
          />
          {arquivo !== null && !docx && (
            <span className="text-xs text-erro">
              Só o formato .docx é lido — salve o documento como .docx no Word.
            </span>
          )}
        </label>
        <Campo
          rotulo="Nome do modelo"
          placeholder={
            arquivo === null ? 'Ex.: Termo padrão' : arquivo.name.replace(/\.docx$/i, '')
          }
          ajuda="Em branco, usa o nome do arquivo."
          value={nome}
          onChange={(e) => setNome(e.target.value)}
        />
        <CampoDeSelecao
          rotulo="Onde vou usar"
          value={uso}
          onChange={(e) => setUso(e.target.value as UsoDoModelo)}
          opcoes={props.usos.map((u) => ({ valor: u.id, rotulo: u.rotulo }))}
        />
        <Aviso tom="informacao">
          Títulos, parágrafos, negrito, listas e tabelas são preservados. Fonte, margens e cabeçalho
          de página não — o PDF usa a diagramação do Deca. Depois de importar, o texto abre no
          editor para conferência.
        </Aviso>
        {importar.isError && <ErroDaApi titulo="Não foi possível importar" erro={importar.error} />}
      </div>
    </Modal>
  );
}
